import {t} from "@lingui/core/macro";
import {css, StyleSheet} from "aphrodite";
import * as React from "react";

import {LabeledTextField} from "@khanacademy/wonder-blocks-form";
import {PhosphorIcon} from "@khanacademy/wonder-blocks-icon";
import {color} from "@khanacademy/wonder-blocks-tokens";
import Tooltip from "@khanacademy/wonder-blocks-tooltip";
import type {Typography} from "@khanacademy/wonder-blocks-typography";

import {PASSWORD_MIN_LENGTH} from "./password-util.ts";

import infoIcon from "@phosphor-icons/core/regular/info.svg";

const invalidPassword = (minLength: number): string =>
    t`Your password must be at least ${minLength} characters long.`;

interface Props {
    /**
     * Provide a label for the field.
     */
    label: string | React.ReactElement<React.ComponentProps<Typography>>;
    /**
     * Provide a description for the field.
     */
    description?: string | React.ReactElement<React.ComponentProps<Typography>>;
    /**
     * Called when the value has changed.
     */
    onChange: (newValue: string) => unknown;
    /**
     * Called when the field input is validated.
     */
    onValidate?: (errorMessage?: string | null | undefined) => unknown;
    /**
     * The initial input value.
     */
    initialValue?: string;
    /**
     * Provide a placeholder for the input field.
     */
    placeholder?: string;
    /**
     * Provide a different empty error message than the default.
     */
    emptyMessage?: string;
    /**
     * Disable the input field.
     */
    disabled?: boolean;
    /**
     * Optional test id for e2e testing.
     * The testId for the internal input element will
     * have "-field" automatically appended.
     */
    testId?: string;
    /**
     * An optional unique identifier for the TextField.
     * If no id is specified, a unique id will be auto-generated.
     */
    id?: string;
    required?: boolean | string;
    tooltip?: string | React.ReactElement;
}

/**
 * @deprecated Use PasswordTextField instead.
 */
export const DeprecatedPasswordTextField = React.forwardRef<
    HTMLInputElement,
    Props
>(
    (
        {
            label,
            description,
            onValidate,
            placeholder,
            disabled,
            testId,
            id,
            required,
            tooltip,
            initialValue = "",
            emptyMessage = "",
            onChange,
        },
        ref,
    ) => {
        const [value, setValue] = React.useState(initialValue);

        const handleChange = React.useCallback(
            (newValue: string) => {
                setValue(newValue);
                onChange(newValue);
            },
            [onChange],
        );

        const validate = React.useCallback(
            (value: string) => {
                if (!value) {
                    return emptyMessage || t`Please create a password.`;
                }
                if (value.length < PASSWORD_MIN_LENGTH) {
                    return invalidPassword(PASSWORD_MIN_LENGTH);
                }
            },
            [emptyMessage],
        );

        return (
            <div className={css(styles.wrapper)}>
                {tooltip && (
                    <Tooltip content={tooltip}>
                        {/* Wrapper div is needed since you cannot apply "outline" to
                    a tooltip */}
                        <div className={css(styles.infoIcon)}>
                            <PhosphorIcon
                                icon={infoIcon}
                                color={color.fadedOffBlack64}
                                size="small"
                                aria-label={t`More Info`}
                            />
                        </div>
                    </Tooltip>
                )}
                <LabeledTextField
                    id={id}
                    testId={testId}
                    type="password"
                    label={label}
                    description={description}
                    placeholder={placeholder}
                    value={value}
                    onChange={handleChange}
                    validate={validate}
                    onValidate={onValidate}
                    disabled={disabled}
                    ref={ref}
                    required={required}
                />
            </div>
        );
    },
);

const styles = StyleSheet.create({
    wrapper: {
        position: "relative",
    },
    infoIcon: {
        cursor: "pointer",

        // Position the icon in the top right corner of the text field
        position: "absolute",
        top: 0,
        insetInlineEnd: 0,

        // Needed to have the icon above the label,
        zIndex: 1,

        ":focus": {
            outline: `${color.blue} auto`,
        },
    },
});
